/**
 * Overlap Validation for Pricing Rules
 *
 * Pure validation functions that check for same-type conflicts:
 * 1. Minimum hour rules vs other minimum hour rules
 * 2. Price adjustments vs other price adjustments
 *
 * Only same-type overlaps are rejected, because the engine picks a single winner
 * within each rule type — two matching rules of the same type are ambiguous.
 * Cross-type overlap is intentionally allowed: minimum hours and price
 * adjustments apply at different stages of the calculation (minimum hours set
 * the base charge, the adjustment is then applied to that subtotal), so a rule
 * of each type can and should coexist on the same day, time, and vehicle.
 */

interface TimeWindow {
  readonly startTime: string | null // HH:MM or null (all-day)
  readonly endTime: string | null // HH:MM or null (all-day)
}

interface RuleBase extends TimeWindow {
  readonly id: string
  readonly name: string
  readonly daysOfWeek: number[]
  readonly vehicleIds: string[] | null
  readonly isActive: boolean
}

interface MinimumHourRule extends RuleBase {
  readonly minimumHours: number
}

interface PriceAdjustmentRule extends RuleBase {
  readonly percentageIncrease: number
}

interface ConflictResult {
  readonly hasConflict: boolean
  readonly conflictDetails: string
}

/**
 * Check if two time windows overlap.
 * All-day (null time) overlaps with any other window.
 */
function doTimeWindowsOverlap(a: TimeWindow, b: TimeWindow): boolean {
  // If either is all-day, they overlap
  if ((a.startTime === null && a.endTime === null) ||
      (b.startTime === null && b.endTime === null)) {
    return true
  }

  // If either has partial null (invalid), treat as no overlap
  if (!a.startTime || !a.endTime || !b.startTime || !b.endTime) {
    return false
  }

  const toMinutes = (time: string): number => {
    const [h, m] = time.split(':').map(Number)
    return h * 60 + m
  }

  const aStart = toMinutes(a.startTime)
  const aEnd = toMinutes(a.endTime)
  const bStart = toMinutes(b.startTime)
  const bEnd = toMinutes(b.endTime)

  // Check ranges accounting for overnight windows
  const aRanges = buildTimeRanges(aStart, aEnd)
  const bRanges = buildTimeRanges(bStart, bEnd)

  return aRanges.some((aRange) =>
    bRanges.some((bRange) =>
      aRange.start <= bRange.end && bRange.start <= aRange.end
    )
  )
}

/**
 * Build time ranges for overlap checking.
 * Overnight ranges (end < start) are split into two segments.
 */
function buildTimeRanges(start: number, end: number): Array<{ start: number; end: number }> {
  if (end < start) {
    // Overnight: split into [start, 1439] and [0, end]
    return [
      { start, end: 1439 },
      { start: 0, end },
    ]
  }
  return [{ start, end }]
}

/**
 * Check if two rules share any common day of week.
 */
function doDaysOverlap(daysA: number[], daysB: number[]): boolean {
  return daysA.some((day) => daysB.includes(day))
}

/**
 * Check if two rules target overlapping vehicles.
 * null vehicle_ids = all vehicles (overlaps with anything).
 */
function doVehiclesOverlap(
  vehicleIdsA: string[] | null,
  vehicleIdsB: string[] | null
): boolean {
  // Either targets all vehicles => overlap
  if (vehicleIdsA === null || vehicleIdsB === null) {
    return true
  }

  // Both target specific vehicles => check intersection
  return vehicleIdsA.some((id) => vehicleIdsB.includes(id))
}

/**
 * Check if a proposed minimum hour rule overlaps with existing minimum hour rules.
 * Returns conflict details if a conflict is found.
 */
export function checkMinimumHourOverlap(
  proposed: MinimumHourRule,
  existingRules: readonly MinimumHourRule[],
  excludeId?: string
): ConflictResult {
  for (const existing of existingRules) {
    // Skip self in edit mode
    if (excludeId && existing.id === excludeId) {
      continue
    }

    // Only check active rules
    if (!existing.isActive) {
      continue
    }

    // Check day overlap
    if (!doDaysOverlap(proposed.daysOfWeek, existing.daysOfWeek)) {
      continue
    }

    // Check time window overlap
    if (!doTimeWindowsOverlap(proposed, existing)) {
      continue
    }

    // Check vehicle overlap
    if (!doVehiclesOverlap(proposed.vehicleIds, existing.vehicleIds)) {
      continue
    }

    // Found a conflict
    return {
      hasConflict: true,
      conflictDetails: `Conflicts with existing rule "${existing.name}" on overlapping days, time window, and vehicles.`,
    }
  }

  return { hasConflict: false, conflictDetails: '' }
}

/**
 * Check if a proposed price adjustment overlaps with existing price adjustments.
 * Prevents ambiguous rules — e.g. a discount and a surge that both match the same
 * day/time/vehicles, where the pricing engine could only apply one of them.
 */
export function checkPriceAdjustmentOverlap(
  proposed: PriceAdjustmentRule,
  existingRules: readonly PriceAdjustmentRule[],
  excludeId?: string
): ConflictResult {
  for (const existing of existingRules) {
    // Skip self in edit mode
    if (excludeId && existing.id === excludeId) {
      continue
    }

    // Only check active rules
    if (!existing.isActive) {
      continue
    }

    // Check day overlap
    if (!doDaysOverlap(proposed.daysOfWeek, existing.daysOfWeek)) {
      continue
    }

    // Check time window overlap
    if (!doTimeWindowsOverlap(proposed, existing)) {
      continue
    }

    // Check vehicle overlap
    if (!doVehiclesOverlap(proposed.vehicleIds, existing.vehicleIds)) {
      continue
    }

    // Found a conflict
    return {
      hasConflict: true,
      conflictDetails: `Conflicts with price adjustment "${existing.name}" on overlapping days, time window, and vehicles. Only one adjustment can apply at a time — adjust the times, days, or vehicles so they don't overlap.`,
    }
  }

  return { hasConflict: false, conflictDetails: '' }
}
